import { BatteryCharging, Plug, Gauge, Zap, Snowflake, Sun } from "lucide-react";
import { isEV, isPureElectric, evScore, evScoreTier } from "@/data/ev";

function Ring({ value = 0, label, sub }) {
  const pct = Math.max(0, Math.min(100, value));
  const r = 34, c = 2 * Math.PI * r, off = c - (pct / 100) * c;
  return (
    <div className="flex flex-col items-center">
      <svg width="88" height="88" viewBox="0 0 88 88" className="-rotate-90">
        <circle cx="44" cy="44" r={r} fill="none" strokeWidth="7" className="ev-gauge-track" />
        <circle cx="44" cy="44" r={r} fill="none" strokeWidth="7" strokeLinecap="round"
          className="ev-gauge-fill" strokeDasharray={c} strokeDashoffset={off} />
      </svg>
      <div className="-mt-[58px] mb-[26px] text-center">
        <div className="ev-num text-lg font-extrabold text-white">{pct}%</div>
      </div>
      <div className="text-sm font-bold text-white">{label}</div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

function Metric({ Icon, label, value, hint }) {
  return (
    <div className="ev-glass ev-glass-active rounded-2xl p-4">
      <div className="flex items-center gap-2 text-[#39FF6A]"><Icon size={18} strokeWidth={1.6} /><span className="text-xs font-bold uppercase tracking-wide text-slate-300">{label}</span></div>
      <div className="ev-num mt-2 text-2xl font-extrabold text-white">{value ?? "—"}</div>
      {hint && <div className="mt-0.5 text-xs text-slate-400">{hint}</div>}
    </div>
  );
}

// Fiche technique EV — shown automatically when the vehicle is electric/hybrid.
// Reads the Pactly `ev` override block when present; otherwise shows the fields
// as "à compléter" so the interface is consistent (data filled by PRO/scraper).
export default function EVSpecs({ car }) {
  if (!car || !isEV(car.fuel)) return null;
  const ev = car.ev || {};
  const pure = isPureElectric(car.fuel);
  const wltp = ev.range_wltp || car.range_wltp;
  const soh = ev.soh != null ? Number(ev.soh) : null;
  const kwh = ev.battery_kwh || car.battery_kwh;
  const chargeKw = ev.charge_kw;
  const range = wltp ? Number(wltp) : null;
  const score = evScore(ev);
  const tier = evScoreTier(score);
  return (
    <section data-testid="ev-specs" className="theme-electric force-dark relative overflow-hidden rounded-3xl border border-[#39FF6A]/15 bg-[#0B132B] p-6">
      <div className="ev-aurora" aria-hidden style={{ inset: "-80% auto auto -40%" }} />
      <div className="relative z-10">
        <div className="mb-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#39FF6A]/12 text-[#39FF6A]">{pure ? <Zap size={18} className="fill-[#39FF6A]" /> : <BatteryCharging size={18} />}</span>
            <div>
              <h2 className="font-display text-lg font-extrabold text-white ev-glow-text">Fiche technique {pure ? "100% Électrique" : "Hybride"}</h2>
              <p className="text-xs text-slate-400">Batterie · Recharge · Autonomie{ev.estimated ? " · valeurs estimées (modèle)" : ""}</p>
            </div>
          </div>
          {score != null && (
            <div data-testid="ev-score" title={`Pactly EV Score · ${tier.label}`}
              className="flex items-center gap-3 rounded-2xl border px-4 py-2.5 ev-glow-border"
              style={{ borderColor: `${tier.color}55`, background: `${tier.color}12` }}>
              <div className="text-right">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Pactly EV Score</div>
                <div className="text-xs font-bold" style={{ color: tier.color }}>{tier.label}</div>
              </div>
              <div className="ev-num text-3xl font-extrabold leading-none" style={{ color: tier.color }}>{score}<span className="text-sm text-slate-500">/100</span></div>
            </div>
          )}
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="ev-glass ev-glass-active flex items-center justify-around rounded-2xl p-4">
            <Ring value={soh != null ? soh : 95} label="Santé batterie" sub={soh != null ? "SOH réel" : "estimé"} />
            <Ring value={range ? Math.min(100, Math.round((range / 600) * 100)) : 70} label="Autonomie" sub={range ? `${range} km WLTP` : "WLTP"} />
          </div>
          <Metric Icon={BatteryCharging} label="Capacité utile" value={kwh ? `${kwh} kWh` : "—"} hint={soh != null ? `SOH ${soh}%` : "état de santé à confirmer"} />
          <Metric Icon={Plug} label="Recharge max" value={chargeKw ? `${chargeKw} kW` : "—"} hint={ev.charge_time_fast ? `Rapide ~${ev.charge_time_fast}` : "temps rapide vs domicile"} />
        </div>
        {range != null && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="ev-glass rounded-2xl p-4">
              <div className="flex items-center gap-2 text-sky-300"><Snowflake size={16} /> <span className="text-xs font-bold uppercase text-slate-300">Réelle hiver</span></div>
              <div className="ev-num mt-1 text-xl font-extrabold text-white">~{Math.round(range * 0.72)} km</div>
            </div>
            <div className="ev-glass rounded-2xl p-4">
              <div className="flex items-center gap-2 text-amber-300"><Sun size={16} /> <span className="text-xs font-bold uppercase text-slate-300">Réelle été</span></div>
              <div className="ev-num mt-1 text-xl font-extrabold text-white">~{Math.round(range * 0.9)} km</div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
